import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { Text, Stack, Link, tokens } from '@hotjar/hotsauce';
import { LOGOUT } from '@hotjar/webapp/constants/routes/auth';
import { LIST as SITE_LIST } from '@hotjar/webapp/constants/routes/site';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { TextHorizontalRule } from '@hotjar/webapp/components/TextHorizontalRule';
import { usePermissionRequiredTracking } from '../hooks/usePermissionRequiredTracking';
import { RequestAccessResult } from '../types';
import { SuccessScreen } from './SuccessScreen';
import { Button } from './Button';
import { Title } from './Title';

const BackToAccountLink = styled(Link)`
  align-self: center;
  ${tokens.text('caption')};
  margin-top: ${tokens.spacer.x2};
`;

interface AuthenticatedScreenProps {
  requestAccessResult: RequestAccessResult;
  requestAccess: (email: string) => void;
  isProcessing: boolean;
  user: { email: string | null };
}

export function AuthenticatedScreen({
  user,
  requestAccess,
  requestAccessResult = RequestAccessResult.NOT_SENT,
  isProcessing = false,
}: AuthenticatedScreenProps): React.ReactElement {
  const { t } = useTranslation();
  const { trackClick } = usePermissionRequiredTracking();
  const { email } = user;

  const onRequestAccessClick = () => {
    trackClick('Request Access');
    requestAccess(email as string);
  };

  if (requestAccessResult === RequestAccessResult.SUCCESS) {
    return (
      <SuccessScreen>
        <BackToAccountLink
          onClick={() => trackClick('Back to account')}
          kind="standalone"
          href={getRoute(SITE_LIST.path)}
        >
          {t('activation:permission-required.authenticated.link')}
        </BackToAccountLink>
      </SuccessScreen>
    );
  }

  return (
    <Stack direction="column" spacing="x0">
      <Title>{t('activation:permission-required.title')}</Title>
      <Text textType="body1" textColor="text_light" css={{ marginBottom: tokens.spacer.x3 }}>
        <Trans i18nKey="activation:permission-required.authenticated.text">
          You’re signed in as <strong>{{ email }}</strong>. Either request access or switch accounts
          to view this page.
        </Trans>
      </Text>
      <Button onClick={onRequestAccessClick} kind="primary" isLoading={isProcessing}>
        {t('activation:permission-required.request-access')}
      </Button>
      <TextHorizontalRule message={t('activation:permission-required.or')} />
      <Button
        as="a"
        onClick={() => trackClick('Switch Account')}
        href={`${getRoute(LOGOUT.path)}${window.location.search}`}
        kind="secondary"
      >
        {t('activation:permission-required.authenticated.secondary-button')}
      </Button>
      <BackToAccountLink
        onClick={() => trackClick('Back to account')}
        kind="standalone"
        href={getRoute(SITE_LIST.path)}
      >
        {t('activation:permission-required.authenticated.link')}
      </BackToAccountLink>
    </Stack>
  );
}
